import {
  Label as HeadlessLabel,
  Listbox as HeadlessListbox,
  type ListboxProps as HeadlessUiListboxProps,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from "@headlessui/react";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { HiMiniCheck, HiOutlineChevronDown } from "react-icons/hi2";

import { Label } from "~/components/ui/forms/label";
import { cn } from "~/utils/classnames";

type ListBoxOption = {
  id?: string | number;
  value: any;
  label: ReactNode;
  labelInInput?: ReactNode;
  onClick?: () => void;
};

type ListboxProps = {
  options: ListBoxOption[];
  label?: string;
  externalSelectedOption?: ListBoxOption | null;
} & HeadlessUiListboxProps<any, any, any>;

export const Listbox = ({
  options,
  label,
  defaultOption,
  externalSelectedOption,
  onChange,
  className,
  ...props
}: ListboxProps) => {
  const [selectedOption, setSelectedOption] = useState<ListBoxOption | null>(
    defaultOption ?? options[0] ?? null
  );

  // Handle internal state changes
  const handleChange = (option: ListBoxOption) => {
    if (onChange) {
      onChange(option);
    }
    setSelectedOption(option);
  };

  // Synchronize internal state with external state
  useEffect(() => {
    if (externalSelectedOption) {
      setSelectedOption(externalSelectedOption);
    }
  }, [externalSelectedOption]);

  return (
    <>
      <HeadlessListbox
        value={selectedOption}
        onChange={handleChange}
        as="div"
        className={cn("flex flex-col", className)}
        {...props}
      >
        {label ? (
          <HeadlessLabel as={Label} className="mb-2 ml-2 block text-sm font-medium text-grey-900">
            {label}
          </HeadlessLabel>
        ) : null}
        <div className="relative flex flex-grow">
          <ListboxButton className="relative w-full cursor-pointer rounded-full border border-grey-300 bg-white px-4 py-2 text-left text-sm text-grey-900">
            <span className="block truncate">
              {selectedOption?.labelInInput ?? selectedOption?.label}
            </span>
            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
              <HiOutlineChevronDown className="h-5 w-5 text-grey-300" aria-hidden="true" />
            </span>
          </ListboxButton>
          <ListboxOptions className="absolute top-full z-10 mt-1 max-h-60 w-full divide-y divide-grey-50 overflow-auto rounded-xl border border-grey-100 bg-white py-1 text-sm shadow-lg">
            {options.map((option) => (
              <ListboxOption
                key={option.id}
                className={({ focus }) =>
                  cn(
                    focus ? "bg-grey-100" : "text-grey-900",
                    option.onClick ? "" : "px-4 py-2",
                    "relative cursor-pointer select-none"
                  )
                }
                value={option}
              >
                {({ selected }) => (
                  <>
                    {option.onClick ? (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          option.onClick?.();
                        }}
                        className={cn(
                          "flex w-full items-center gap-3 truncate px-4 py-2 text-left",
                          selected ? "!font-medium" : "font-normal"
                        )}
                      >
                        {option.label}
                        {selected ? (
                          <HiMiniCheck className="h-4 w-4 stroke-1 text-primary" />
                        ) : null}
                      </button>
                    ) : (
                      <span
                        className={cn(
                          "flex items-center gap-3 truncate",
                          selected ? "!font-medium" : "font-normal"
                        )}
                      >
                        {option.label}
                        {selected ? (
                          <HiMiniCheck className="h-4 w-4 stroke-1 text-primary" />
                        ) : null}
                      </span>
                    )}
                  </>
                )}
              </ListboxOption>
            ))}
          </ListboxOptions>
        </div>
      </HeadlessListbox>
    </>
  );
};
